import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const quickLinks = [
  { href: '/', icon: 'ri-home-4-line', label: 'Startseite', desc: 'Zurück zur Übersicht' },
  { href: '/leistungen', icon: 'ri-tools-line', label: 'Leistungen', desc: 'Was wir anbieten' },
  { href: '/projekte', icon: 'ri-image-2-line', label: 'Projekte', desc: 'Unsere Referenzen' },
  { href: '/kontakt', icon: 'ri-customer-service-2-line', label: 'Kontakt', desc: 'Jetzt anfragen' },
];

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      navigate('/');
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden">
      <Navbar />

      <main className="flex-1 flex flex-col">
        {/* Hero area */}
        <section className="relative flex-1 flex items-center justify-center py-24 sm:py-32 bg-[#F8F7F4] overflow-hidden">
          {/* Big 404 background text */}
          <span
            className="absolute inset-0 flex items-center justify-center text-[20rem] sm:text-[28rem] font-black text-gray-100 select-none pointer-events-none leading-none"
            aria-hidden="true"
          >
            404
          </span>

          <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 text-center">
            {/* Icon badge */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0D2B4E] rounded-2xl mb-6 mx-auto">
              <i className="ri-map-pin-line text-4xl text-white" />
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 leading-tight">
              Diese Seite wurde nicht gefunden
            </h1>

            <p className="text-gray-500 text-base sm:text-lg mb-2 leading-relaxed">
              Die Seite{' '}
              <code className="bg-gray-200 text-gray-700 text-sm px-2 py-0.5 rounded font-mono">
                {location.pathname}
              </code>{' '}
              existiert leider nicht.
            </p>

            <p className="text-gray-400 text-sm mb-8">
              Sie werden in{' '}
              <span className="font-bold text-[#0D2B4E] tabular-nums">{countdown}</span>{' '}
              Sekunden automatisch weitergeleitet.
            </p>

            {/* Progress bar */}
            <div className="w-48 h-1 bg-gray-200 rounded-full mx-auto mb-10 overflow-hidden">
              <div
                className="h-full bg-[#C8A96E] rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${(countdown / 10) * 100}%` }}
              />
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-[#0D2B4E] text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line text-lg" />
                </div>
                Zur Startseite
              </Link>
              <a
                href="tel:+4921027398070"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#0D2B4E] text-[#0D2B4E] px-7 py-3.5 rounded-lg font-semibold hover:bg-[#0D2B4E] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg" />
                </div>
                02102 7398070
              </a>
            </div>
          </div>
        </section>

        {/* Quick links */}
        <section className="py-14 sm:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-center text-sm font-semibold text-gray-400 uppercase tracking-widest mb-8">
              Vielleicht suchen Sie das?
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {quickLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="group flex flex-col items-center text-center p-5 rounded-xl border border-gray-100 bg-[#F8F7F4] hover:border-[#0D2B4E] hover:bg-[#0D2B4E] transition-all duration-200 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-white group-hover:bg-white/10 rounded-xl mb-3 transition-colors">
                    <i className={`${link.icon} text-2xl text-[#0D2B4E] group-hover:text-white transition-colors`} />
                  </div>
                  <span className="font-semibold text-gray-900 group-hover:text-white text-sm transition-colors">
                    {link.label}
                  </span>
                  <span className="text-xs text-gray-400 group-hover:text-white/70 mt-0.5 transition-colors">
                    {link.desc}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
